import { config } from '../config/env';
import { supabase } from './supabase';

export interface LiveKitConnectionDetails {
  token: string;
  url: string;
  roomName: string;
  participantIdentity: string;
  participantName: string;
}

/**
 * Request a LiveKit token from the edge function
 */
export async function getLiveKitToken(): Promise<LiveKitConnectionDetails> {
  // Use getUser() to get fresh token validation, not cached getSession()
  const { data: userData, error: userError } = await supabase.auth.getUser();

  if (userError || !userData.user) {
    throw new Error('No active session');
  }

  // Get the current session for the access token
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('No active session');
  }

  const response = await fetch(config.livekitTokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionData.session.access_token}`,
      apikey: config.supabase.anonKey,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to get LiveKit token');
  }

  return response.json();
}

export interface DispatchAgentResponse {
  success: boolean;
  dispatchId: string;
  roomName: string;
  agentName: string;
}

/**
 * Dispatch the agent to a room after the user has joined
 */
export async function dispatchAgent(roomName: string): Promise<DispatchAgentResponse> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error('No active session');
  }

  const response = await fetch(config.dispatchAgentEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionData.session.access_token}`,
      apikey: config.supabase.anonKey,
    },
    body: JSON.stringify({ roomName }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to dispatch agent');
  }

  return response.json();
}
